import React from 'react';

import {
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';

import { CharterCabinClass, CharterFlight, SeatInfo, SeatSelectionState, Traveller } from '../charter.types';
import { CHARTER_SEAT_CLEAR_BLOCKED_MESSAGE } from '../common/charterSeatMessages';
import { formatCabinClass, toCharterCabinClass } from '../common/flightAssignments';
import { formatTravellerName } from '../common/formatTravellerName';

export type TravellerSeatTableProps = {
  travellers: Array<Traveller>;
  flights: Array<CharterFlight>;
  seatSelections: SeatSelectionState;
  onSeatChange: (travellerId: string, flightId: string, seatId?: string) => void;
  getSeatOptions: (traveller: Traveller, flight: CharterFlight) => Array<SeatInfo>;
  getSeatPrice: (flightId: string, cabin: CharterCabinClass | null, seatId?: string) => number | undefined;
  getSelectionError: (traveller: Traveller, flight: CharterFlight) => string | null;
  getTravellerCabin: (traveller: Traveller, flight: CharterFlight) => CharterCabinClass | null;
  onCabinChange: (travellerId: string, flightId: string, cabin: CharterCabinClass) => void;
  getCabinOptions: (flight: CharterFlight) => Array<CharterCabinClass>;
};

export default function TravellerSeatTable({
  travellers,
  flights,
  seatSelections,
  onSeatChange,
  getSeatOptions,
  getSeatPrice,
  getSelectionError,
  getTravellerCabin,
  onCabinChange,
  getCabinOptions,
}: TravellerSeatTableProps): React.JSX.Element {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width={64}>No.</TableCell>
            <TableCell>First, Last Name</TableCell>
            <TableCell>Passenger Type</TableCell>
            {flights.map((flight) => (
              <React.Fragment key={flight.id}>
                <TableCell>{`${flight.depAirportCode} → ${flight.arrAirportCode}`} Cabin</TableCell>
                <TableCell>{`${flight.depAirportCode} → ${flight.arrAirportCode}`} Seat Selected</TableCell>
                <TableCell>Price</TableCell>
              </React.Fragment>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {travellers.map((traveller, index) => (
            <TableRow key={traveller.id} hover>
              <TableCell>{index + 1}</TableCell>
              <TableCell>{formatTravellerName(traveller)}</TableCell>
              <TableCell>{traveller.passengerType}</TableCell>
              {flights.map((flight) => {
                const availableSeats = getSeatOptions(traveller, flight);
                const selection = seatSelections[flight.id]?.[traveller.id] ?? undefined;
                const cabinClass = getTravellerCabin(traveller, flight);
                const price = getSeatPrice(flight.id, cabinClass, selection);
                const errorText = getSelectionError(traveller, flight);

                return (
                  <React.Fragment key={`${traveller.id}-${flight.id}`}>
                    <TableCell>
                      <TextField
                        select
                        fullWidth
                        value={cabinClass ?? ''}
                        onChange={(event) => {
                          const nextCabin = toCharterCabinClass(event.target.value);
                          if (nextCabin) {
                            onCabinChange(traveller.id, flight.id, nextCabin);
                          }
                        }}
                        disabled={traveller.passengerType === 'INF'}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (selected: unknown): React.ReactNode => {
                            if (traveller.passengerType === 'INF') {
                              return 'Not applicable';
                            }
                            if (!selected) {
                              return 'Select cabin';
                            }
                            return formatCabinClass(toCharterCabinClass(String(selected)));
                          },
                        }}
                      >
                        {getCabinOptions(flight).map((cabin) => (
                          <MenuItem key={cabin} value={cabin}>
                            {formatCabinClass(cabin)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        fullWidth
                        value={selection || ''}
                        onChange={(event) => onSeatChange(traveller.id, flight.id, event.target.value || undefined)}
                        placeholder="Select seat"
                        disabled={traveller.passengerType === 'INF' || availableSeats.length === 0}
                        error={Boolean(errorText)}
                        helperText={errorText ?? undefined}
                        SelectProps={{
                          displayEmpty: true,
                          MenuProps: {
                            PaperProps: {
                              sx: { maxHeight: 280 },
                            },
                          },
                          // Custom render to display the selected seat's displayLabel
                          renderValue: (selected: unknown): React.ReactNode => {
                            if (!selected) {
                              return traveller.passengerType === 'INF' ? 'Not applicable' : 'Select seat';
                            }
                            const seat = availableSeats.find((s) => (s.primaryColumn ?? s.seatId) === selected);
                            return seat?.displayLabel ?? seat?.seatId ?? String(selected);
                          },
                        }}
                      >
                        <MenuItem
                          value=""
                          disabled={traveller.passengerType !== 'INF' && Boolean(selection)}
                          title={
                            traveller.passengerType !== 'INF' && selection
                              ? CHARTER_SEAT_CLEAR_BLOCKED_MESSAGE
                              : undefined
                          }
                        >
                          {traveller.passengerType === 'INF' ? 'Not applicable' : 'Select seat'}
                        </MenuItem>
                        {availableSeats.map((seat) => (
                          // Use primaryColumn for value (sent to backend), displayLabel for display
                          <MenuItem key={seat.seatId} value={seat.primaryColumn ?? seat.seatId}>
                            {seat.displayLabel ?? seat.seatId}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>{selection ? (price ? `$${price}` : 'Free') : '—'}</TableCell>
                  </React.Fragment>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
